import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Send } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PasswordInput, StrengthRules } from "@/components/auth/PasswordInput";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { OptionSelect } from "@/components/data/FieldInput";
import { PageHeader, StatusBadge } from "@/components/data/StatusBadge";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg, fmtDate } from "@/lib/api";

const ACTION_FR = { voir: "Consulter", creer: "Créer", modifier: "Modifier", supprimer: "Supprimer", valider: "Valider", cloturer: "Clôturer", annuler: "Annuler", exporter: "Exporter", parametrer: "Gérer param." };

function ActiveSwitch({ u, onSaved }) {
  const { user, can } = useAuth();
  const toggle = async (v) => {
    try {
      await api.put(`/users/${u.id}`, { active: v });
      toast.success(v ? "Accès activé" : "Accès désactivé");
      onSaved();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return <Switch checked={u.active !== false} disabled={!can("utilisateurs", "modifier") || u.id === user.id} onCheckedChange={toggle} data-testid={`user-active-toggle-${u.id}`} />;
}

function UserDialog({ open, onClose, user, roles, onSaved }) {
  const [f, setF] = useState({});
  useEffect(() => {
    if (open) setF(user ? { name: user.name, email: user.email, telephone: user.telephone || "", role_id: user.role_id, active: user.active !== false, password: "" }
      : { name: "", email: "", telephone: "", role_id: null, active: true, password: "", send_invitation: true });
  }, [open, user]);
  const save = async () => {
    try {
      if (user) await api.put(`/users/${user.id}`, f);
      else await api.post("/users", f);
      toast.success("Utilisateur enregistré");
      onClose();
      onSaved();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="user-dialog">
        <DialogHeader><DialogTitle>{user ? "Modifier l'utilisateur" : "Nouvel utilisateur"}</DialogTitle></DialogHeader>
        <div className="grid sm:grid-cols-2 gap-3">
          <div><Label>Nom complet *</Label><Input value={f.name || ""} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="user-name-input" /></div>
          <div><Label>Email *</Label><Input value={f.email || ""} disabled={!!user} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="user-email-input" /></div>
          <div><Label>Téléphone</Label><Input value={f.telephone || ""} onChange={(e) => setF({ ...f, telephone: e.target.value })} /></div>
          <div><Label>Rôle *</Label><div className="mt-1"><OptionSelect options={roles.map((r) => ({ value: r.id, label: r.name }))} value={f.role_id} onChange={(v) => setF({ ...f, role_id: v })} testId="user-role-select" /></div></div>
          {!user && (
            <div className="sm:col-span-2 flex items-center gap-2"><Switch checked={!!f.send_invitation} onCheckedChange={(v) => setF({ ...f, send_invitation: v, password: "" })} data-testid="user-invitation-switch" /><Label>Envoyer un lien d'invitation par e-mail (l'utilisateur choisit son mot de passe, lien valable 48 h)</Label></div>
          )}
          {(user || !f.send_invitation) && (
            <div><Label>{user ? "Nouveau mot de passe (optionnel)" : "Mot de passe *"}</Label><PasswordInput autoComplete="new-password" value={f.password || ""} onChange={(e) => setF({ ...f, password: e.target.value })} testId="user-password-input" />{f.password && <StrengthRules value={f.password} />}</div>
          )}
          <div className="flex items-center gap-2 mt-6"><Switch checked={!!f.active} onCheckedChange={(v) => setF({ ...f, active: v })} data-testid="user-active-switch" /><Label>Compte actif</Label></div>
        </div>
        <DialogFooter><Button onClick={save} data-testid="user-save-button">Enregistrer</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RoleDialog({ open, onClose, role, onSaved }) {
  const { meta } = useAuth();
  const [f, setF] = useState({ name: "", description: "", permissions: {} });
  useEffect(() => {
    if (open) setF(role ? { name: role.name, description: role.description || "", permissions: role.permissions || {} } : { name: "", description: "", permissions: {} });
  }, [open, role]);
  const toggle = (m, a) => setF((s) => {
    const cur = new Set(s.permissions[m] || []);
    if (cur.has(a)) cur.delete(a); else cur.add(a);
    return { ...s, permissions: { ...s.permissions, [m]: [...cur] } };
  });
  const toggleRow = (m) => setF((s) => {
    const all = (s.permissions[m] || []).length === meta.actions.length;
    return { ...s, permissions: { ...s.permissions, [m]: all ? [] : [...meta.actions] } };
  });
  const save = async () => {
    try {
      if (role) await api.put(`/roles/${role.id}`, f);
      else await api.post("/roles", f);
      toast.success("Rôle enregistré");
      onClose();
      onSaved();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="role-dialog">
        <DialogHeader><DialogTitle>{role ? "Modifier le rôle" : "Nouveau rôle"}</DialogTitle></DialogHeader>
        <div className="grid sm:grid-cols-2 gap-3">
          <div><Label>Nom du rôle *</Label><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="role-name-input" /></div>
          <div><Label>Description</Label><Input value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} /></div>
        </div>
        <div className="overflow-x-auto border rounded-md mt-2">
          <table className="w-full text-xs" data-testid="permissions-matrix">
            <thead className="bg-muted/60"><tr><th className="text-left px-3 py-2">Module / menu</th>{meta.actions.map((a) => <th key={a} className="px-2 py-2">{ACTION_FR[a]}</th>)}</tr></thead>
            <tbody>
              {meta.modules.map((m) => (
                <tr key={m.key} className="border-t">
                  <td className="px-3 py-1.5 font-medium"><button type="button" className="text-left hover:text-primary" onClick={() => toggleRow(m.key)} data-testid={`perm-row-${m.key}`}>{m.label}</button></td>
                  {meta.actions.map((a) => (
                    <td key={a} className="text-center"><Checkbox checked={(f.permissions[m.key] || []).includes(a)} onCheckedChange={() => toggle(m.key, a)} data-testid={`perm-${m.key}-${a}`} /></td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-[11px] text-muted-foreground">Cliquez sur le nom d'un module pour tout cocher / décocher. « Clôturer » : clôture des journées TFJ et des semaines de commissions. « Gérer param. » : listes paramétrables et paramètres généraux.</p>
        <DialogFooter><Button onClick={save} data-testid="role-save-button">Enregistrer</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function UsersPage() {
  const resend = async (u) => {
    try {
      await api.post(`/users/${u.id}/invitation`);
      toast.success(`Invitation mise en envoi pour ${u.email} — suivez la livraison dans Paramètres des e-mails`);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const { can } = useAuth();
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [ud, setUd] = useState(null);
  const [rd, setRd] = useState(null);
  const load = useCallback(() => {
    api.get("/users").then((r) => setUsers(r.data)).catch((e) => toast.error(errMsg(e)));
    api.get("/roles").then((r) => setRoles(r.data)).catch(() => {});
  }, []);
  useEffect(() => {
    load();
  }, [load]);
  const delRole = async (r) => {
    try {
      await api.delete(`/roles/${r.id}`);
      toast.success("Rôle supprimé");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div>
      <PageHeader title="Utilisateurs & permissions" subtitle="Comptes créés par l'administrateur. Permissions par module, menu et action." />
      <Tabs defaultValue="users">
        <TabsList className="mb-4"><TabsTrigger value="users" data-testid="tab-users">Utilisateurs</TabsTrigger><TabsTrigger value="roles" data-testid="tab-roles">Rôles & permissions</TabsTrigger></TabsList>
        <TabsContent value="users">
          <div className="bg-card border rounded-lg">
            <div className="p-3 border-b flex justify-end">{can("utilisateurs", "creer") && <Button size="sm" onClick={() => setUd({})} data-testid="user-create-button"><Plus className="h-4 w-4 mr-1" />Nouvel utilisateur</Button>}</div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="users-table">
                <thead className="bg-muted/60 text-[11px] uppercase tracking-wider text-muted-foreground"><tr><th className="px-3 py-2.5 text-left">Nom</th><th className="px-3 text-left">Email</th><th className="px-3 text-left">Rôle</th><th className="px-3 text-left">Statut</th><th className="px-3 text-left">Dernière connexion</th><th /></tr></thead>
                <tbody>
                  {users.map((u) => (
                    <tr key={u.id} className="border-t" data-testid="users-row">
                      <td className="px-3 py-2 font-medium">{u.name}</td><td className="px-3">{u.email}</td><td className="px-3">{u.role_name}</td>
                      <td className="px-3"><div className="flex items-center gap-2"><ActiveSwitch u={u} onSaved={load} /><StatusBadge value={u.active !== false ? "actif" : "inactif"} />{u.invitation_pending && <span className="text-[10px] uppercase tracking-wider text-amber-700" data-testid={`user-pending-${u.id}`}>Invitation en attente</span>}</div></td>
                      <td className="px-3 text-xs text-muted-foreground">{fmtDate(u.last_login)}</td>
                      <td className="px-2 text-right whitespace-nowrap">
                        {u.invitation_pending && can("utilisateurs", "modifier") && <Button variant="ghost" size="sm" className="h-8 text-xs" onClick={() => resend(u)} data-testid={`user-resend-invite-${u.id}`}><Send className="h-3.5 w-3.5 mr-1" />Renvoyer l'invitation</Button>}
                        {can("utilisateurs", "modifier") && <Button variant="ghost" size="icon" onClick={() => setUd(u)} data-testid={`user-edit-${u.id}`}><Pencil className="h-4 w-4" /></Button>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </TabsContent>
        <TabsContent value="roles">
          <div className="flex justify-end mb-3">{can("utilisateurs", "creer") && <Button size="sm" onClick={() => setRd({})} data-testid="role-create-button"><Plus className="h-4 w-4 mr-1" />Nouveau rôle</Button>}</div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {roles.map((r) => (
              <div key={r.id} className="bg-card border rounded-lg p-4" data-testid="role-card">
                <div className="flex justify-between items-start">
                  <div><div className="font-bold">{r.name}</div><div className="text-xs text-muted-foreground">{r.description}</div></div>
                  {!r.is_admin && (
                    <div className="flex">
                      {can("utilisateurs", "modifier") && <Button variant="ghost" size="icon" onClick={() => setRd(r)} data-testid={`role-edit-${r.id}`}><Pencil className="h-4 w-4" /></Button>}
                      {can("utilisateurs", "supprimer") && <Button variant="ghost" size="icon" onClick={() => delRole(r)}><Trash2 className="h-4 w-4" /></Button>}
                    </div>
                  )}
                </div>
                <div className="text-[11px] text-muted-foreground mt-3">
                  {r.is_admin ? "Accès complet à tous les modules" : `${Object.values(r.permissions || {}).filter((v) => v.length).length} module(s) autorisé(s)`}
                </div>
              </div>
            ))}
          </div>
        </TabsContent>
      </Tabs>
      <UserDialog open={!!ud} user={ud?.id ? ud : null} roles={roles} onClose={() => setUd(null)} onSaved={load} />
      <RoleDialog open={!!rd} role={rd?.id ? rd : null} onClose={() => setRd(null)} onSaved={load} />
    </div>
  );
}
